import { createRoot } from "react-dom/client";
import axios from "axios";

import App, { AppProps } from "@PublicationReader/App";
import { PublicationI } from "@PublicationReader/@types/Publication";
import {
  onAppCloseEvent,
  appMountedBodyClasses,
} from "@PublicationReader/constants";

// Props from pubreader button data attributes
interface AppDatasetProps {
  isPreview?: boolean;
  apiUrl: string;
  apiDownloadUrl: string;
  id: string;
}

const setURLSearchParams = (key: string, value: string) => {
  const params = new URLSearchParams(window.location.search);
  params.set(key, value);
  window.history.pushState({}, "", `${location.pathname}?${params}`);
};

const attachPublicationReaderListeners = () => {
  const pubReadButtons = document.querySelectorAll(
    ".publication-open"
  ) as NodeListOf<HTMLButtonElement>;

  pubReadButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      getPropsAndRender(btn);
    });
  });

  // Open app if publication ID is in query string
  if (
    window.location.search.indexOf("pub=") > -1 &&
    pubReadButtons.length > 0
  ) {
    getPropsAndRender(pubReadButtons[0]);
  }
};

const fetchPub = async ({
  apiUrl,
  id,
  isPreview,
}: Omit<AppDatasetProps, "apiDownloadUrl">): Promise<PublicationI> => {
  let url = `${apiUrl}/${id}`;

  if (isPreview) {
    url += "?preview=true";
  }

  const { data } = await axios.get(url);
  return data;
};

const getPropsAndRender = async (pubReadBtn: HTMLButtonElement) => {
  const props = {
    isPreview: pubReadBtn.dataset.preview === "true" ? true : false,
    apiUrl: pubReadBtn.dataset.apiurl ?? "",
    apiDownloadUrl: pubReadBtn.dataset.apidownloadurl ?? "",
    id: pubReadBtn.dataset.id ?? "",
  } as AppDatasetProps;

  const publication = await fetchPub(props);

  pubReadBtn.setAttribute("aria-expanded", "true");
  setURLSearchParams("pub", props.id);
  renderApp({
    publication,
    ...props,
  });
};

const renderApp = (props: AppProps) => {
  let rootEl = document.getElementById("app") as HTMLDivElement;

  if (!rootEl) {
    rootEl = document.createElement("div");
    rootEl.setAttribute("id", "app");
    document.body.prepend(rootEl);
  }

  const root = createRoot(rootEl);
  root.render(<App {...props} />);

  window.addEventListener(onAppCloseEvent, () => {
    document.body.classList.remove(...appMountedBodyClasses);

    if (document.body.contains(rootEl)) {
      root.unmount();
    }

    // Set scroll position to top when closing
    window.scrollTo(0, 0);
  });
};

document.addEventListener("DOMContentLoaded", () => {
  attachPublicationReaderListeners();
});
